import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import {
  MatchList,
  RegistryChoice,
  RegistryModel,
  RegistryModelSearch,
  pickStyle,
  registryChoices,
} from './RegistryModelSearch';
import { Badge, Button, Field, SegmentedControl, inputStyle, tokens } from './ui/controls';

type RunRow = { id: number; run_name?: string; robot_name?: string; start_time?: string | null };
type DatasetRow = { dataset_id: string; name: string; version: string };
type LabelRow = { name: string; kind: 'box' | 'image'; detail: string };
type ChosenModel = { name?: string; s3_uri?: string };

function record(value: unknown): Record<string, unknown> {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return { ...(value as Record<string, unknown>) };
  }
  return {};
}

function formatWhen(value: unknown) {
  if (!value) return '';
  return String(value).replace('T', ' ').replace(/\.\d+$/, '');
}

export function InferenceComparisonJobForm({
  spec,
  onChange,
}: {
  spec: Record<string, unknown>;
  onChange: (spec: Record<string, unknown>) => void;
}) {
  const data = record(spec.data);
  const dataset = record(spec.dataset);
  const rawType = String(data.input_type || (dataset.name ? 'dataset' : ''));
  const chosen = rawType === 'etl_job' || rawType === 'dataset' ? rawType : '';
  const models = Array.isArray(spec.models) ? (spec.models as ChosenModel[]) : [];
  const selectedRun = data.etl_run_id == null ? '' : String(data.etl_run_id);
  const datasetName = String(data.dataset_name || dataset.name || '');
  const datasetVersion = String(data.dataset_version || dataset.version || '');
  const selectedDataset = datasetName && datasetVersion ? `${datasetName}@${datasetVersion}` : '';
  const selectedLabels = Array.isArray(data.label_classes) ? data.label_classes.map(String) : null;

  const [modelQuery, setModelQuery] = useState('');
  const [choices, setChoices] = useState<RegistryChoice[]>([]);
  const [modelError, setModelError] = useState('');
  const [runQuery, setRunQuery] = useState('');
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [runError, setRunError] = useState('');
  const [datasetQuery, setDatasetQuery] = useState('');
  const [datasets, setDatasets] = useState<DatasetRow[]>([]);
  const [datasetError, setDatasetError] = useState('');
  const [labels, setLabels] = useState<LabelRow[]>([]);
  const [labelError, setLabelError] = useState('');
  const specRef = useRef(spec);
  specRef.current = spec;

  useEffect(() => {
    api.dataviewer.modelRegistry()
      .then((res) => {
        const models = (res.data as { models?: RegistryModel[] }).models || [];
        setChoices(registryChoices(models));
        setModelError('');
      })
      .catch((err: any) => setModelError(err.response?.data?.detail || err.message || 'Could not list models'));
    api.dataviewer.listRegistryDatasets()
      .then((res) => {
        setDatasets(res.data.datasets || []);
        setDatasetError('');
      })
      .catch((err: any) => setDatasetError(err.response?.data?.detail || err.message || 'Could not list datasets'));
  }, []);

  useEffect(() => {
    if (chosen !== 'etl_job') return undefined;
    const handle = window.setTimeout(() => {
      api.dataviewer.getETLRuns({
        status: 'COMPLETED',
        run_name: runQuery.trim() || undefined,
        limit: 50,
        sort_by: 'start_time',
      })
        .then((res) => {
          setRuns((res.data.runs || []) as RunRow[]);
          setRunError('');
        })
        .catch((err: any) => setRunError(err.response?.data?.detail || err.message || 'Could not list ETL jobs'));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [chosen, runQuery]);

  useEffect(() => {
    let cancelled = false;
    const applyLabels = (next: LabelRow[], source: Record<string, unknown>) => {
      if (cancelled) return;
      setLabels(next);
      setLabelError('');
      const current = specRef.current;
      const currentData = record(current.data);
      if (Array.isArray(currentData.label_classes)) return;
      onChange({ ...current, data: { ...currentData, ...source, label_classes: next.map((row) => row.name) } });
    };
    if (chosen === 'dataset' && datasetName && datasetVersion) {
      api.dataviewer.datasetSummary(datasetName, datasetVersion)
        .then((res) => {
          applyLabels(
            (res.data.classes || []).map((row) => ({
              name: row.name,
              kind: row.boxes > 0 ? 'box' as const : 'image' as const,
              detail: row.boxes > 0 ? `${row.boxes} boxes` : `${row.rows} images`,
            })),
            { input_type: 'dataset', dataset_name: datasetName, dataset_version: datasetVersion },
          );
        })
        .catch((err: any) => {
          if (!cancelled) setLabelError(err.response?.data?.detail || err.message || 'Could not read dataset labels');
        });
      return () => { cancelled = true; };
    }
    if (chosen === 'etl_job' && selectedRun) {
      api.dataviewer.etlRunLabels(Number(selectedRun))
        .then((res) => {
          applyLabels(
            (res.data.labels || []).map((row) => ({
              name: row.name,
              kind: row.kind,
              detail: row.kind === 'box' ? 'boxes' : 'image label',
            })),
            { input_type: 'etl_job', etl_run_id: Number(selectedRun) },
          );
        })
        .catch((err: any) => {
          if (!cancelled) setLabelError(err.response?.data?.detail || err.message || 'Could not read ETL labels');
        });
      return () => { cancelled = true; };
    }
    setLabels([]);
    return undefined;
  }, [chosen, datasetName, datasetVersion, selectedRun, onChange]);

  const datasetMatches = useMemo(() => {
    const needle = datasetQuery.trim().toLowerCase();
    const rows = needle
      ? datasets.filter((row) => `${row.name}@${row.version}`.toLowerCase().includes(needle))
      : datasets;
    return rows.slice(0, 40);
  }, [datasets, datasetQuery]);

  const writeData = (nextData: Record<string, unknown>, extra?: Record<string, unknown>) => {
    onChange({ ...spec, ...extra, data: nextData });
  };

  const chooseType = (inputType: 'etl_job' | 'dataset') => {
    const next: Record<string, unknown> = { input_type: inputType };
    if (inputType === 'dataset' && datasetName && datasetVersion) {
      next.dataset_name = datasetName;
      next.dataset_version = datasetVersion;
    }
    if (inputType === 'etl_job' && selectedRun) next.etl_run_id = Number(selectedRun);
    onChange({
      ...spec,
      data: next,
      dataset: inputType === 'dataset' && datasetName ? { name: datasetName, version: datasetVersion } : undefined,
    });
  };

  const toggleModel = (choice: RegistryChoice) => {
    const exists = models.some((model) => model.s3_uri === choice.s3_uri);
    const next = exists
      ? models.filter((model) => model.s3_uri !== choice.s3_uri)
      : [...models, { name: choice.label, source: 's3_uri', s3_uri: choice.s3_uri }];
    onChange({ ...spec, models: next });
  };

  const toggleLabel = (name: string) => {
    const current = selectedLabels || [];
    const next = current.includes(name) ? current.filter((item) => item !== name) : [...current, name];
    writeData({ ...data, label_classes: next });
  };

  const confidence = spec.confidence_threshold == null ? '0.25' : String(spec.confidence_threshold);
  const inputOptions: Array<{ id: '' | 'etl_job' | 'dataset'; label: string }> = [
    { id: 'etl_job', label: 'ETL job' },
    { id: 'dataset', label: 'Dataset' },
  ];

  return (
    <div style={{ display: 'grid', gap: '0.9rem' }}>
      <div style={{ display: 'grid', gap: '0.45rem' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: tokens.textMuted }}>
          Models ({models.length} selected, at least 2)
        </span>
        {models.length > 0 && (
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
            {models.map((model) => (
              <Badge key={String(model.s3_uri)} tone="info">{model.name || model.s3_uri}</Badge>
            ))}
          </div>
        )}
        <RegistryModelSearch
          choices={choices}
          error={modelError}
          query={modelQuery}
          onQuery={setModelQuery}
          selectedUris={models.map((model) => String(model.s3_uri || ''))}
          onSelect={toggleModel}
          placeholder="Search the model registry"
        />
        {models.some((model) => !String(model.s3_uri || '').endsWith('.pt')) ? (
          <span style={{ fontSize: '0.8rem', color: tokens.danger }}>
            This template names models another way. Choose registered .pt weights.
          </span>
        ) : null}
      </div>

      <div style={{ display: 'grid', gap: '0.45rem' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: tokens.textMuted }}>Input</span>
        <SegmentedControl
          options={inputOptions}
          value={chosen}
          onChange={(id) => {
            if (id === 'etl_job' || id === 'dataset') chooseType(id);
          }}
        />
        <span style={{ fontSize: '0.8rem', color: tokens.textMuted }}>
          Compare the models on a completed ETL job, or on a registry dataset.
        </span>
        {rawType && !chosen ? (
          <span style={{ fontSize: '0.8rem', color: tokens.danger }}>
            This template used {rawType}. Choose an ETL job or a dataset.
          </span>
        ) : null}
      </div>

      {chosen === 'etl_job' && (
        <div style={{ display: 'grid', gap: '0.45rem' }}>
          <Field label="Completed ETL job">
            <input
              value={runQuery}
              onChange={(event) => setRunQuery(event.target.value)}
              placeholder="Search run name"
              style={inputStyle}
            />
          </Field>
          {runError ? (
            <span style={{ fontSize: '0.85rem', color: tokens.danger }}>{runError}</span>
          ) : (
            <MatchList empty="No completed ETL jobs match that search.">
              {runs.map((run) => (
                <button
                  key={run.id}
                  type="button"
                  onClick={() => writeData({ input_type: 'etl_job', etl_run_id: run.id })}
                  style={pickStyle(String(run.id) === selectedRun)}
                >
                  {run.run_name || `Run ${run.id}`}
                  {run.robot_name ? ` · ${run.robot_name}` : ''}
                  {run.start_time ? ` · ${formatWhen(run.start_time)}` : ''}
                </button>
              ))}
            </MatchList>
          )}
        </div>
      )}

      {chosen === 'dataset' && (
        <div style={{ display: 'grid', gap: '0.45rem' }}>
          <Field label="Dataset">
            <input
              value={datasetQuery}
              onChange={(event) => setDatasetQuery(event.target.value)}
              placeholder="Search dataset"
              style={inputStyle}
            />
          </Field>
          {datasetError ? (
            <span style={{ fontSize: '0.85rem', color: tokens.danger }}>{datasetError}</span>
          ) : (
            <MatchList empty="No datasets match that search.">
              {datasetMatches.map((row) => {
                const label = `${row.name}@${row.version}`;
                return (
                  <button
                    key={row.dataset_id}
                    type="button"
                    onClick={() => onChange({
                      ...spec,
                      dataset: { name: row.name, version: row.version },
                      data: { input_type: 'dataset', dataset_name: row.name, dataset_version: row.version },
                    })}
                    style={pickStyle(label === selectedDataset)}
                  >
                    {label}
                  </button>
                );
              })}
            </MatchList>
          )}
        </div>
      )}

      {chosen && (labels.length > 0 || labelError) && (
        <div style={{ display: 'grid', gap: '0.45rem' }}>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: tokens.textMuted }}>Labels</span>
          <span style={{ fontSize: '0.8rem', color: tokens.textMuted }}>
            Selected labels are scored against each model. Clear them to compare the models only.
          </span>
          {labelError ? (
            <span style={{ fontSize: '0.85rem', color: tokens.danger }}>{labelError}</span>
          ) : (
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
              {labels.map((label) => {
                const active = (selectedLabels || []).includes(label.name);
                return (
                  <Button key={label.name} variant={active ? 'primary' : 'default'} onClick={() => toggleLabel(label.name)}>
                    {label.name} · {label.detail}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <Field label="Confidence">
        <input
          value={confidence}
          onChange={(event) => {
            const value = event.target.value.trim();
            onChange({ ...spec, confidence_threshold: value === '' ? undefined : Number(value) });
          }}
          style={inputStyle}
        />
      </Field>
    </div>
  );
}
